import { useMemo } from 'react';
import { useSERPState } from '~/core/state/SERPStateContext';
import { CategoryResults_State } from '~/core/state/types/state-results.schema';
import { QuerySet_State } from '~/core/state/types/state-sets.type';
import SRError from '~/utils/errors/srError';
import { parseToNumberOrZero } from '~/utils/numberParsed';

export type UseCategoryResultsItem = {
    result: CategoryResults_State;
    title: string;
    state: QuerySet_State;
};

export type UseCategoryResultsResult = {
    categoryResults: UseCategoryResultsItem[];
};

export const useCategoryResults = (): UseCategoryResultsResult => {
    const state = useSERPState();

    const categoryResults = useMemo(() => {
        return state.results.sets
            .reduce((acc, set) => {
                const setState = state.sets.find((setC) => setC.querySet.identifier === set.querySetIdentifier);

                if (!setState) {
                    throw new SRError(`Config not found for identifier ${set.querySetIdentifier}`);
                }

                if (setState.layoutPosition.includes('serp-categories')) {
                    acc.push({
                        title: setState.querySet.name,
                        result: set,
                        state: setState,
                    });
                }

                return acc;
            }, [] as UseCategoryResultsItem[])
            .sort((a, b) => {
                // This sort will never be called if the array is of length 1.
                // And if length is more than 1, than the last thing is a number
                const aSufix = a.state.layoutPosition.split('-').at(-1) ?? '0';
                const bSufix = b.state.layoutPosition.split('-').at(-1) ?? '0';
                const aIndex = parseToNumberOrZero(aSufix);
                const bIndex = parseToNumberOrZero(bSufix);
                return aIndex - bIndex;
            });
    }, [state.results, state.sets]);

    return { categoryResults };
};
